import React, { createContext, useContext, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from './ToastContext';

interface PipedreamContextType {
    connect: (appSlug?: string) => void;
    isInitialized: boolean;
    isConnecting: boolean;
}

const PipedreamContext = createContext<PipedreamContextType | undefined>(undefined);

export function PipedreamProvider({ children }: { children: ReactNode }) {
    const [isConnecting, setIsConnecting] = useState(false);
    const { showSuccess, showError } = useToast();

    // Using Connect Link approach instead of SDK iframe
    // This is more reliable and avoids cross-origin issues
    const connect = async (appSlug: string = 'pipedream') => {
        if (isConnecting) {
            console.log("Pipedream: Already connecting, ignoring duplicate request");
            return;
        }

        setIsConnecting(true);
        console.log(`Pipedream: Starting connection flow for app: ${appSlug}`);

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                showError('Please log in to connect integrations');
                setIsConnecting(false);
                return;
            }

            // Fetch token from our backend
            console.log("Pipedream: Fetching connect token...");
            const response = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pipedream-create-token`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ app: appSlug })
                }
            );

            if (!response.ok) {
                const errText = await response.text();
                console.error("Pipedream: Token fetch failed:", response.status, errText);
                showError('Failed to initialize connection');
                setIsConnecting(false);
                return;
            }

            const data = await response.json();
            console.log("Pipedream: Token received, connect_link_url:", data.connect_link_url);

            if (!data.connect_link_url) {
                console.error("Pipedream: No connect_link_url in response");
                showError('Connection URL not available');
                setIsConnecting(false);
                return;
            }

            // Add app parameter to the connect link URL
            const connectUrl = new URL(data.connect_link_url);
            if (appSlug !== 'pipedream') {
                connectUrl.searchParams.set('app', appSlug);
            }

            // Open in popup window
            const width = 600;
            const height = 700;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;

            const popup = window.open(
                connectUrl.toString(),
                'pipedream_connect',
                `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
            );

            if (!popup) {
                showError('Popup blocked. Please allow popups for this site.');
                setIsConnecting(false);
                return;
            }

            // Check if popup is closed
            const checkClosed = setInterval(async () => {
                if (popup.closed) {
                    clearInterval(checkClosed);
                    setIsConnecting(false);

                    // Sync Pipedream accounts to our database
                    console.log("Pipedream: Popup closed, syncing accounts...");
                    try {
                        const syncResponse = await fetch(
                            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pipedream-sync-accounts`,
                            {
                                method: 'POST',
                                headers: {
                                    'Authorization': `Bearer ${session.access_token}`,
                                    'Content-Type': 'application/json',
                                },
                            }
                        );

                        if (syncResponse.ok) {
                            const syncData = await syncResponse.json();
                            console.log("Pipedream: Accounts synced:", syncData);
                            if (syncData.accounts_found > 0) {
                                showSuccess(`Connected! ${syncData.accounts_found} integration(s) synced.`);
                            } else {
                                showSuccess('Connection window closed. Check Connected tab for status.');
                            }
                        } else {
                            console.error("Pipedream: Sync failed");
                            showSuccess('Connection window closed. Check Connected tab for status.');
                        }
                    } catch (syncError) {
                        console.error("Pipedream: Error syncing accounts:", syncError);
                        showSuccess('Connection window closed. Check Connected tab for status.');
                    }

                    window.dispatchEvent(new Event('integration:connected'));
                }
            }, 500);

        } catch (e) {
            console.error("Pipedream: Exception during connect:", e);
            setIsConnecting(false);
            showError('Failed to open connection window');
        }
    };

    return (
        <PipedreamContext.Provider value={{ connect, isInitialized: true, isConnecting }}>
            {children}
        </PipedreamContext.Provider>
    );
}

export function usePipedream() {
    const context = useContext(PipedreamContext);
    if (context === undefined) {
        throw new Error('usePipedream must be used within a PipedreamProvider');
    }
    return context;
}

